import { Box, Button, Collapse, Stack, Typography } from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ImageUploadQueueRow from './ImageUploadQueueRow'
import ClearAllIcon from '@mui/icons-material/ClearAll';

export type ImageUploadStatus = 'uploading' | 'processing' | 'done' | 'error' | 'canceled'

export interface ImageUploadQueueItem {
  id: string
  file: File
  name: string
  size: number
  previewUrl?: string
  progress: number
  progressKnown?: boolean
  status: ImageUploadStatus
  error?: string
  canRetry?: boolean
  fingerprint?: string
  isDuplicate?: boolean
}

interface Props {
  uploads: ImageUploadQueueItem[]
  onCancel: (id: string) => void
  onRetry: (id: string) => void
  onRemove: (id: string) => void
  onClearCompleted: () => void
  onClearFailed: () => void
}

export default function ImageUploadQueue(props: Props) {
  const [t] = useTranslation('account-v2')

  const completedCount = useMemo(() => {
    return props.uploads.filter(upload => upload.status === 'done').length
  }, [props.uploads])

  const hasFailedUploads = props.uploads.some(upload => upload.status === 'error' || upload.status === 'canceled')

  const renderUploadRow = useCallback((upload: ImageUploadQueueItem) => (
    <ImageUploadQueueRow
      key={upload.id}
      upload={upload}
      onCancel={props.onCancel}
      onRetry={props.onRetry}
      onRemove={props.onRemove}
    />
  ), [props.onCancel, props.onRemove, props.onRetry])


  return <Collapse in={props.uploads.length > 0}>
    <Box
      component="section"
      sx={{
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 2,
        p: 1.5,
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{t('Uploads')}</Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('{{completed}} of {{total}} complete', {
              completed: completedCount,
              total: props.uploads.length,
            })}
          </Typography>
        </Stack>
        <Stack direction="row" sx={{ flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1 }}>
          {hasFailedUploads ? <Button
            size="small"
            color="secondary"
            onClick={props.onClearFailed}
            startIcon={<ClearAllIcon />}
          >{t('Clear failed')}</Button> : null}
          {completedCount > 0 ? <Button
            size="small"
            color="secondary"
            onClick={props.onClearCompleted}
            startIcon={<ClearAllIcon />}
          >{t('Clear completed')}</Button> : null}
        </Stack>
      </Stack>

      <Stack
        spacing={1}
        sx={{
          maxHeight: {
            xs: 'none',
            md: 280,
          },
          overflowY: 'auto',
        }}
      >
        {props.uploads.map(renderUploadRow)}
      </Stack>
    </Box>
  </Collapse>
}
